/**
 * Anciennes adresses des paramètres (`/parametres…`, refonte v4 : « Mon
 * entreprise » vit sous `/entreprise`). Notifications, e-mails, signets,
 * retours de connexion (Google, Pennylane) et anciens onglets (`?tab=`,
 * `?volet=`) y mènent encore : chacune retrouve sa page, les autres
 * paramètres de l'adresse sont gardés.
 */

export const ENTREPRISE_PATH = '/entreprise';

/** Anciennes sections (`/parametres/<section>` ou `?tab=<section>`). */
const SECTIONS: Record<string, string> = {
  compte: '/profil/compte/acces',
  utilisateurs: '/entreprise/membres',
  roles: '/entreprise/roles',
  entreprise: '/entreprise/metiers',
  abonnement: '/entreprise/abonnement',
  // Les véhicules se gèrent dans le Matériel.
  flotte: '/stock?onglet=vehicules',
  // « Identité visuelle et documents » : l'apparence des documents.
  identite: '/entreprise/apparence',
  integrations: '/entreprise/integrations',
  modeles: '/entreprise/baremes',
  relances: '/entreprise/relances',
  avis: '/entreprise/avis',
  documents: '/entreprise/documents',
};

/** Anciens onglets de « Fonctionnement de l'entreprise » (`?volet=`). */
const VOLETS: Record<string, string> = {
  metier: '/entreprise/metiers',
  terrain: '/entreprise/terrain',
  heures: '/entreprise/temps',
  ventes: '/entreprise/devis',
};

/** Sous-pages : préfixe ancien → préfixe nouveau, du plus précis au plus large. */
const PREFIXES: [string, string][] = [
  ['/parametres/utilisateurs/', '/entreprise/membres/'],
  ['/parametres/roles/', '/entreprise/roles/'],
  ['/parametres/integrations/', '/entreprise/integrations/'],
  ['/parametres/modeles/machines/', '/entreprise/catalogue/'],
  ['/parametres/modeles/', '/entreprise/baremes/'],
];

/**
 * Anciens paramètres lus une fois, puis retirés de l'adresse (UI.md §4) :
 * `[onglet de l'accueil, volet du fonctionnement, retour de Google]`.
 */
const LEGACY_PARAMS = ['tab', 'volet', 'google'] as const;

function withSearch(target: string, params: URLSearchParams, hash: string): string {
  const [path, query = ''] = target.split('?');
  const merged = new URLSearchParams(query);
  for (const [key, value] of params) if (!merged.has(key)) merged.set(key, value);
  const search = merged.toString();
  return `${path}${search ? `?${search}` : ''}${hash}`;
}

/**
 * La nouvelle adresse d'une ancienne adresse des paramètres. `pathname` commence
 * par `/parametres` ; `search` et `hash` viennent de l'adresse ouverte.
 */
export function legacySettingsPath(pathname: string, search = '', hash = ''): string {
  const params = new URLSearchParams(search);
  const [tabName, voletName, googleName] = LEGACY_PARAMS;
  const path = pathname.replace(/\/+$/, '');
  // Retour de « Lier mon compte Google » (serveur) : la page de son compte.
  if (path === '/parametres' && params.get(googleName) !== null) return withSearch('/profil/compte', params, hash);
  const tab = path === '/parametres' ? params.get(tabName) : null;
  params.delete(tabName);
  const section = tab ?? (path.startsWith('/parametres/') ? path.slice('/parametres/'.length) : '');
  if (section === '') return withSearch(ENTREPRISE_PATH, params, hash);
  // Le catalogue de machines était un onglet des barèmes (ancre).
  if (section === 'modeles' && hash === '#catalogue-machines') return withSearch('/entreprise/catalogue', params, '');
  if (section === 'entreprise') {
    const volet = params.get(voletName) ?? '';
    params.delete(voletName);
    return withSearch(VOLETS[volet] ?? SECTIONS.entreprise!, params, hash);
  }
  if (SECTIONS[section]) return withSearch(SECTIONS[section], params, hash);
  // Google Agenda se relie depuis le planning, pas depuis les intégrations.
  if (section === 'integrations/google') return withSearch('/planning/google-agenda', params, hash);
  for (const [from, to] of PREFIXES) {
    if (`${path}/`.startsWith(from) && path.length >= from.length) {
      return withSearch(`${to}${path.slice(from.length)}`.replace(/\/+$/, ''), params, hash);
    }
  }
  return withSearch(ENTREPRISE_PATH, params, hash);
}
